import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { runGoldAnalysisAdmin } from "@/lib/analyze-gold.functions";
import { diagnoseDetector } from "@/lib/diagnose-detector.functions";
import { Inbox, ArrowRight, X, Sparkles, Trash2, Loader2, Play, Stethoscope } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { fetchDraftsByStatus, rejectDraft, type SignalDraft } from "@/lib/signal-drafts";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { MacroBadge } from "@/components/dashboard/MacroBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldX } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/signals/drafts")({
  component: DraftsInboxPage,
});

function DraftsInboxPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [rejectTarget, setRejectTarget] = useState<SignalDraft | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [tab, setTab] = useState<"pending" | "rejected" | "published">("pending");

  const drafts = useQuery({
    queryKey: ["signal-drafts", tab],
    queryFn: () => fetchDraftsByStatus(tab),
    enabled: !!user,
  });

  // Realtime: any insert/update on signal_drafts should refresh the list.
  useEffect(() => {
    const channel = supabase
      .channel("signal-drafts-inbox")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "signal_drafts" },
        () => {
          qc.invalidateQueries({ queryKey: ["signal-drafts"] });
          qc.invalidateQueries({ queryKey: ["signal-drafts-count"] });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc]);

  const reject = useMutation({
    mutationFn: async (args: { id: string; reason: string }) =>
      rejectDraft(args.id, args.reason.trim() || null),
    onSuccess: () => {
      toast.success("Draft rejected");
      qc.invalidateQueries({ queryKey: ["signal-drafts"] });
      qc.invalidateQueries({ queryKey: ["signal-drafts-count"] });
      setRejectTarget(null);
      setRejectReason("");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const runAnalysisFn = useServerFn(runGoldAnalysisAdmin);
  const runAi = useMutation({
    mutationFn: async () => {
      return (await runAnalysisFn()) as {
        ok?: boolean;
        skipped?: string;
        candidates?: number;
        drafts_created?: number;
        skipped_duplicates?: number;
        rejected_low_confidence?: number;
        error?: string;
      };
    },
    onSuccess: (data) => {
      if (data.skipped === "market_closed") {
        toast.info("Gold market is closed. Try again during trading hours.");
        return;
      }
      if (data.error) {
        toast.error(`AI run failed: ${data.error}`);
        return;
      }
      const created = data.drafts_created ?? 0;
      if (created > 0) {
        toast.success(`${created} new draft(s) created`);
      } else {
        toast.message(
          `No new setup. Candidates: ${data.candidates ?? 0}, dupes: ${data.skipped_duplicates ?? 0}, rejected: ${data.rejected_low_confidence ?? 0}`,
        );
      }
      qc.invalidateQueries({ queryKey: ["signal-drafts"] });
      qc.invalidateQueries({ queryKey: ["signal-drafts-count"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const diagnoseFn = useServerFn(diagnoseDetector);
  const diagnose = useMutation({
    mutationFn: async () => (await diagnoseFn()) as Record<string, unknown>,
    onSuccess: (data) => {
      console.log("[detector-diagnostics]", data);
      const counts = (data as { counts?: Record<string, number> }).counts ?? {};
      const sweepTrace =
        (data as {
          sweepTrace?: Array<{
            skippedReason: string | null;
            linkage_failure_reason: string | null;
          }>;
        }).sweepTrace ?? [];
      const bosAudit =
        (data as {
          bosAudit?: {
            bos_total: number;
            bos_unique_broken_swings: number;
            bos_pre_sweep_broken_swing_count: number;
            bos_reuse_count: {
              max_reuse: number;
              mean_reuse: number;
              bos_reused_by_multiple_sweeps_count: number;
            };
          };
        }).bosAudit ?? null;
      const reasons: Record<string, number> = {};
      for (const t of sweepTrace) {
        const key = t.linkage_failure_reason ?? t.skippedReason ?? "passed";
        reasons[key] = (reasons[key] ?? 0) + 1;
      }
      const reasonStr =
        Object.keys(reasons).length === 0
          ? "no recent sweeps"
          : Object.entries(reasons)
              .sort((a, b) => b[1] - a[1])
              .map(([k, v]) => `${k}:${v}`)
              .join(" ");
      const bosLine = bosAudit
        ? ` BOS audit: total=${bosAudit.bos_total} uniq_swings=${bosAudit.bos_unique_broken_swings} pre_sweep=${bosAudit.bos_pre_sweep_broken_swing_count} reuse(max=${bosAudit.bos_reuse_count.max_reuse} mean=${bosAudit.bos_reuse_count.mean_reuse} multi=${bosAudit.bos_reuse_count.bos_reused_by_multiple_sweeps_count}).`
        : "";
      toast.message(
        `Swings:${counts.swings ?? 0} Sweeps:${counts.sweeps ?? 0} (recent:${counts.recentSweeps ?? 0}) BOS:${counts.bos ?? 0} FVG:${counts.unmitigatedFvgs ?? 0}/${counts.fvgs ?? 0} OB:${counts.orderBlocks ?? 0} Cands:${counts.candidates ?? 0}`,
        {
          description: `Linkage: ${reasonStr}.${bosLine} Full payload in console.`,
          duration: 20000,
        },
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const list = drafts.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gold">
            <Sparkles className="h-3.5 w-3.5" /> AI inbox
          </div>
          <h1 className="mt-1 font-display text-3xl font-bold">Signal drafts</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Review AI-generated trade setups before publishing. Only setups with 80%+ confidence reach this inbox.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => diagnose.mutate()}
            disabled={diagnose.isPending}
          >
            {diagnose.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Stethoscope className="h-4 w-4" />
            )}
            Diagnose detector
          </Button>
          <Button
            variant="gold"
            size="sm"
            onClick={() => runAi.mutate()}
            disabled={runAi.isPending}
          >
            {runAi.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Run AI now
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="rejected">Rejected (macro audit)</TabsTrigger>
          <TabsTrigger value="published">Published</TabsTrigger>
        </TabsList>
      </Tabs>

      {drafts.isLoading && (
        <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-card p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading drafts...
        </div>
      )}

      {drafts.error && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {(drafts.error as Error).message}
        </div>
      )}

      {!drafts.isLoading && list.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border/60 bg-card/50 p-12 text-center">
          <Inbox className="h-10 w-10 text-muted-foreground" />
          <div>
            <div className="font-display text-lg font-semibold">Inbox zero</div>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              No AI drafts pending review. New high-confidence setups will appear here automatically and trigger a push notification.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {list.map((d) => (
          <DraftCard
            key={d.id}
            draft={d}
            onPublish={() =>
              navigate({
                to: "/admin/signals/new",
                search: { draft: d.id },
              })
            }
            onReject={() => {
              setRejectTarget(d);
              setRejectReason("");
            }}
          />
        ))}
      </div>

      <Dialog
        open={!!rejectTarget}
        onOpenChange={(open) => {
          if (!open) {
            setRejectTarget(null);
            setRejectReason("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject this draft?</DialogTitle>
            <DialogDescription>
              The draft will be archived and pushed out of the inbox. Add an optional reason for the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason">Reason (optional)</Label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="e.g. SL too tight, weak liquidity sweep"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={reject.isPending}
              onClick={() => {
                if (rejectTarget) {
                  reject.mutate({ id: rejectTarget.id, reason: rejectReason });
                }
              }}
            >
              <Trash2 className="h-4 w-4" /> Reject draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DraftCard({
  draft,
  onPublish,
  onReject,
}: {
  draft: SignalDraft;
  onPublish: () => void;
  onReject: () => void;
}) {
  const isBuy = draft.type === "Buy";
  const created = new Date(draft.created_at);
  const ageMin = Math.max(0, Math.round((Date.now() - created.getTime()) / 60000));
  const ageLabel =
    ageMin < 1 ? "just now" : ageMin < 60 ? `${ageMin}m ago` : `${Math.round(ageMin / 60)}h ago`;

  return (
    <div className="rounded-xl border border-border/60 bg-card p-5 transition-colors hover:border-gold/40">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-lg font-bold">{draft.asset}</span>
          <span
            className={`rounded-md px-2 py-0.5 text-xs font-semibold ${
              isBuy
                ? "bg-emerald-500/15 text-emerald-400"
                : "bg-red-500/15 text-red-400"
            }`}
          >
            {draft.type.toUpperCase()}
          </span>
          <span className="rounded-md border border-border/60 px-2 py-0.5 text-xs text-muted-foreground">
            {draft.order_type}
          </span>
          <span className="rounded-md border border-border/60 px-2 py-0.5 text-xs text-muted-foreground">
            {draft.timeframe}
          </span>
          {draft.bias && (
            <span className="rounded-md border border-border/60 px-2 py-0.5 text-xs text-muted-foreground">
              Bias: {draft.bias}
            </span>
          )}
          <span className="rounded-md bg-gold-muted/30 px-2 py-0.5 text-xs font-semibold text-gold">
            {draft.confidence_pct}% confidence
          </span>
          <MacroBadge alignment={draft.macro_alignment} />
          {draft.macro_reason_code && draft.status === "rejected" && (
            <span className="inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-destructive">
              <ShieldX className="h-3 w-3" /> {draft.macro_reason_code}
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">{ageLabel}</span>
      </div>

      {draft.status !== "rejected" || draft.entry > 0 ? (
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
          <Level label="Entry" value={draft.entry} />
          <Level label="SL" value={draft.sl} tone="loss" />
          <Level label="TP1" value={draft.tp1} tone="win" />
          <Level label="TP2" value={draft.tp2} tone="win" />
        </div>
      ) : null}

      {draft.note && (
        <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{draft.note}</p>
      )}

      {draft.status === "pending" && (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onReject}>
            <X className="h-4 w-4" /> Reject
          </Button>
          <Button variant="gold" size="sm" onClick={onPublish}>
            Review and publish <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function Level({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "win" | "loss";
}) {
  const color =
    tone === "win"
      ? "text-emerald-400"
      : tone === "loss"
        ? "text-red-400"
        : "text-foreground";
  return (
    <div className="rounded-md border border-border/60 bg-muted/30 px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`font-mono text-sm font-semibold ${color}`}>
        {value.toFixed(2)}
      </div>
    </div>
  );
}
